/**
 * Invoices List Page
 * 
 * Main page component for displaying and managing invoices.
 */

import { renderInvoiceList, initInvoiceListHandlers } from '../../components/invoices/InvoiceList.js';
import { apiClient } from '../../services/api.js';
import { t } from '../../i18n/translations.js';
import { getStore, actions } from '../../services/store.js';
import { toast } from '../../services/toast.js';

/**
 * Page state
 */
let state = {
  invoices: [],
  filters: {},
  pagination: { page: 1, totalPages: 1, totalItems: 0 },
  view: 'card',
  loading: true,
  error: null,
};

/**
 * Update page state and re-render
 */
function setState(newState) {
  state = { ...state, ...newState };
  render();
}

/**
 * Fetch invoices from API
 */
async function fetchInvoices(page = 1, filters = {}) {
  setState({ loading: true, error: null });

  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: '20',
      ...filters,
    });

    const response = await apiClient.get(`/v1/invoices?${params}`);

    // Handle wrapped API response: { success, data: { success, data: [...], pageInfo } }
    // The API wraps responses twice, so we need to unwrap properly
    let data = response.data || response;
    // If there's another nested data object (double-wrapped), unwrap it
    if (data && data.data !== undefined) {
      data = data.data;
    }
    const items = Array.isArray(data) ? data : (data.data || data.items || []);
    const pageInfo = (typeof data === 'object' && !Array.isArray(data)) ? (data.pageInfo || {}) : {};

    setState({
      invoices: items,
      pagination: {
        page: pageInfo.page || page,
        totalPages: pageInfo.totalPages || 1,
        totalItems: pageInfo.totalItems || items.length || 0,
      },
      filters,
      loading: false,
    });
  } catch (err) {
    console.error('Failed to fetch invoices:', err);
    setState({
      invoices: [],
      pagination: {
        page: 1,
        totalPages: 1,
        totalItems: 0,
      },
      loading: false,
      error: err.message || 'Failed to load invoices',
    });
  }
}

/**
 * Initialize global handlers
 */
function initHandlers() {
  // View handlers
  window.setInvoiceView = (view) => {
    setState({ view });
  };

  // Pagination handlers
  window.goToInvoicePage = (page) => {
    fetchInvoices(page, state.filters);
  };

  // Filter handlers
  window.applyInvoiceFilters = () => {
    const status = document.getElementById('filter-status')?.value || '';
    const vendor = document.getElementById('filter-vendor')?.value || '';
    const dateFrom = document.getElementById('filter-date-from')?.value || '';
    const dateTo = document.getElementById('filter-date-to')?.value || '';
    
    const filters = {};
    if (status) filters.status = status;
    if (vendor) filters.vendor = vendor;
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    
    fetchInvoices(1, filters);
  };

  window.clearInvoiceFilters = () => {
    fetchInvoices(1, {});
  };

  // Action handlers
  window.viewInvoice = (id) => {
    window.location.hash = `invoices/${id}`;
  };

  window.analyzeInvoice = (id) => {
    console.log('Analyze invoice:', id);
    // Implement analysis trigger
  };

  window.showInvoiceActions = (id, event) => {
    // Close any existing dropdown
    const existingDropdown = document.querySelector('.action-dropdown');
    if (existingDropdown) {
      existingDropdown.remove();
    }

    // Find the invoice data
    const invoice = state.invoices.find(inv => inv.id === id);
    if (!invoice) return;

    // Create dropdown menu
    const dropdown = document.createElement('div');
    dropdown.className = 'action-dropdown';
    dropdown.innerHTML = `
      <div class="dropdown-menu">
        <button class="dropdown-item" onclick="window.viewInvoice('${id}')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          ${t('common.viewDetails') || 'View Details'}
        </button>
        <button class="dropdown-item" onclick="window.navigateToAnalysis('${id}')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="11" y1="8" x2="11" y2="14"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
          ${t('invoices.analyze') || 'Analyze'}
        </button>
        <button class="dropdown-item" onclick="window.linkInvoiceContract('${id}')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
          ${t('invoices.linkContract') || 'Link Contract'}
        </button>
        <button class="dropdown-item" onclick="window.downloadInvoice('${id}')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          ${t('common.download') || 'Download'}
        </button>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item dropdown-item-danger" onclick="window.deleteInvoice('${id}')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
          ${t('common.delete') || 'Delete'}
        </button>
      </div>
    `;

    // Position the dropdown near the button
    const button = event?.target?.closest('.btn-icon') || document.querySelector(`[data-invoice-id="${id}"] .btn-icon:last-child`);
    if (button) {
      const rect = button.getBoundingClientRect();
      dropdown.style.position = 'fixed';
      dropdown.style.top = `${rect.bottom + 4}px`;
      dropdown.style.right = `${window.innerWidth - rect.right}px`;
    }

    document.body.appendChild(dropdown);

    // Close dropdown when clicking outside
    const closeDropdown = (e) => {
      if (!dropdown.contains(e.target) && e.target !== button) {
        dropdown.remove();
        document.removeEventListener('click', closeDropdown);
      }
    };

    // Close dropdown when clicking any item inside
    dropdown.querySelectorAll('.dropdown-item').forEach(item => {
      item.addEventListener('click', () => {
        dropdown.remove();
        document.removeEventListener('click', closeDropdown);
      });
    });

    // Delay adding listener to avoid immediate close
    setTimeout(() => {
      document.addEventListener('click', closeDropdown);
    }, 10);
  };

  // Navigate to analysis page
  window.navigateToAnalysis = (id) => {
    window.location.hash = `invoices/${id}/analysis`;
  };

  // Link contract to invoice
  window.linkInvoiceContract = (id) => {
    window.location.hash = `invoices/${id}`;
    // The detail page has contract linking functionality
  };

  // Download invoice
  window.downloadInvoice = async (id) => {
    try {
      const response = await apiClient.get(`/v1/invoices/${id}/download`);
      const data = response.data?.data || response.data || response;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        toast.warning(t('invoices.noDocument') || 'No document available for download');
      }
    } catch (err) {
      console.error('Failed to download invoice:', err);
      const errorMsg = err.message || '';
      if (errorMsg.includes('No source document') || errorMsg.includes('not found')) {
        toast.warning(t('invoices.noDocument') || 'No document file available for this invoice');
      } else {
        toast.error(t('common.error') || 'Failed to download invoice');
      }
    }
  };

  // Delete invoice
  window.deleteInvoice = async (id) => {
    if (!confirm(t('invoices.confirmDelete') || 'Are you sure you want to delete this invoice?')) {
      return;
    }
    try {
      await apiClient.delete(`/v1/invoices/${id}`);
      // Refresh the list
      fetchInvoices(state.pagination.page, state.filters);
    } catch (err) {
      console.error('Failed to delete invoice:', err);
      toast.error(t('common.error') || 'Failed to delete invoice');
    }
  };

  window.toggleAllInvoices = (checked) => {
    const checkboxes = document.querySelectorAll('.invoice-checkbox');
    checkboxes.forEach(cb => cb.checked = checked);
  };

  // Initialize component handlers
  initInvoiceListHandlers();
}

/**
 * Render the page
 */
function render() {
  const container = document.getElementById('app-content');
  if (!container) return;

  if (state.loading) {
    container.innerHTML = `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="page invoices-page">
      ${renderInvoiceList({
        invoices: state.invoices,
        filters: state.filters,
        pagination: state.pagination,
        view: state.view,
        showFilters: true,
      })}
    </div>
  `;
}

/**
 * Page component export
 */
export default {
  /**
   * Initialize the page
   */
  async init() {
    initHandlers();
    await fetchInvoices();
  },

  /**
   * Render the page content
   */
  render() {
    return `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
  },

  /**
   * Called after page is mounted
   */
  async mounted() {
    await this.init();
  },

  /**
   * Cleanup on page leave
   */
  destroy() {
    // Cleanup handlers
    delete window.setInvoiceView;
    delete window.goToInvoicePage;
    delete window.applyInvoiceFilters;
    delete window.clearInvoiceFilters;
    delete window.viewInvoice;
    delete window.analyzeInvoice;
    delete window.showInvoiceActions;
    delete window.toggleAllInvoices;
  },
};
